import React, { useContext, useEffect, useState } from 'react';
import {
    GelForm, GelFormField, GelTextInput,
    useGelFormData,
    GelButton,
    GelBoxLayout,
    GelContainerLite,
    GelScreenDetectorContext,
    GelLabel,
    GelLink,
    GelIcon,
    GelRowLayout,
    GelHeading3,
    GelParagraph,
    GelSpinner
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { AccountApi } from '../../common/api/auth.provider';
import { UserContext } from '../../common/usercontext/user.context';
import { useOktaAuth } from '@okta/okta-react';
import FundBanner from "../../components/fundBanner";
import { AdobeClickType, OKTA_ERROR_ACCOUNT_LOCKED, OktaRecentSmsErrorCode } from '../../constants/constants';
import { useNavigate } from 'react-router-dom';
import SESSION_STORAGE_KEY from '../../constants/storage.constant';
import { AdobeAnalytics } from '../../common/analytics/adobe-analytics';
import { useCookies } from 'react-cookie';
import { useGetTwoFactorPageContentData } from '../../common/api/graphQLDataFetch';
import { FormatHelper } from '../../common/formatHelper.common';

const TwoFactorAuth = () => {
    const { global: { sizeNone, sizeBaseUnit, themeColorTextDanger, sizeBaseX2 } } = getGelTokens()

    const { data, isFetched } = useGetTwoFactorPageContentData()

    const { oktaAuth } = useOktaAuth();
    const { isLargerThanSmScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const [mfaStatusMessage, setMfaStatusMessage] = useState("");
    const [mfaInvalidCodeMessage, setMfaInvalidCodeMessage] = useState("");
    const [isAccountLocked, setAccountLocked] = useState(false);
    const [{ tpid }] = useCookies(["tpid"])
    const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const { contextData } = useContext(UserContext)
    // Use a ref to persist countdown state across renders
    const isMfaResendActiveRef = React.useRef(false);
    const lastMfaSentRef = React.useRef(0);

    const navigate = useNavigate();

    useEffect(() => {
        AdobeAnalytics.PushPageViewEvent(tpid)
        ProcessMfaCode(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        formData,
        onFormDataChange,
        onFormDataReset,
        resetForm
    } = useGelFormData({
        verificationCode: '',
        memberNumber: ''
    });

    const onSubmit = () => {
        AdobeAnalytics.PushClickEvent(tpid, data?.verifyButtonText, AdobeClickType.Button)

        AccountApi.verifyMfaCode(contextData.memberDetails?.factorId!, contextData.memberDetails?.stateToken!, formData.verificationCode)
            .then(response => response.json())
            .then(verificationResp => {
                console.log("okta response ->", verificationResp)
                if (verificationResp?.status === "SUCCESS") {
                    if (contextData.memberDetails?.oktaEmail) {
                        window.sessionStorage.setItem(SESSION_STORAGE_KEY.SEND_ACTIVATION_EMAIL, contextData.memberDetails?.oktaEmail);
                    }
                    oktaAuth.signInWithRedirect({ sessionToken: verificationResp.sessionToken });
                }
                else {
                    if (verificationResp?.errorLink === OKTA_ERROR_ACCOUNT_LOCKED) {
                        setAccountLocked(true);
                        setMfaStatusMessage(data?.errorMessageContent.accountLockedMsg);
                        setMfaInvalidCodeMessage('');
                    }
                    else {
                        setMfaInvalidCodeMessage(data?.errorMessageContent.verificationFailedMsg);
                    }
                }
            }).catch(httpError => {
                console.log('MFA verification failed', httpError);
            });
    };


    const ReSendMfaCode = () => {
        AdobeAnalytics.PushClickEvent(tpid, data?.resendCodeText, AdobeClickType.Link);
        if (!isMfaResendActiveRef.current) {
            ProcessMfaCode(true);
        }
    }

    const ProcessMfaCode = (isResendMode: boolean) => {
        // Helper function to handle cooldown logic and display status message
        const handleCooldown = (cooldown: number, statusMessage: string) => {
            const now = Date.now();
            const timePassed = now - lastMfaSentRef.current;

            if (timePassed < cooldown) {
                let secondsLeft = Math.ceil((cooldown - timePassed) / 1000);
                setMfaStatusMessage(FormatHelper.stringFormat(statusMessage, secondsLeft.toString()));

                const intervalId = setInterval(() => {
                    secondsLeft -= 1;
                    if (secondsLeft <= 0) {
                        clearInterval(intervalId);
                        setMfaStatusMessage('');
                        isMfaResendActiveRef.current = false;
                    } else {
                        setMfaStatusMessage(FormatHelper.stringFormat(statusMessage, secondsLeft.toString()));
                    }
                }, 1000);
                return true;
            }
            return false;
        };

        const cooldown = 30 * 1000;

        if (lastMfaSentRef.current && handleCooldown(cooldown, data?.errorMessageContent.smsSentRecentlyMsg)) {
            return;
        }

        AccountApi.sendMfaCode(contextData.memberDetails?.factorId!, contextData.memberDetails?.stateToken!)
            .then(response => response.json())
            .then(mfaData => {
                if (mfaData?.errorCode) {
                    if (mfaData.errorCode === OktaRecentSmsErrorCode) {
                        lastMfaSentRef.current = Date.now();
                    } else {
                        setMfaStatusMessage(data?.errorMessageContent.sendSmsFailed);
                    }
                } else {
                    lastMfaSentRef.current = Date.now();

                    if (isResendMode && handleCooldown(cooldown, data?.errorMessageContent.newSmsSentRecentlyMsg)) {
                        isMfaResendActiveRef.current = true;
                        return;
                    }
                }
            })
            .catch(httpError => {
                console.log('failed to send MFA sms', httpError);
                setMfaStatusMessage(data?.errorMessageContent.sendSmsFailed);
            });
    };

    const navigateToMobileUpdate = () => {
        AdobeAnalytics.PushClickEvent(tpid, data?.resetMobileLink, AdobeClickType.Link)
        navigate("/confirmmember");
    }

    const MFAErrorMessage = ({ message }) => (
        <GelLabel small style={{ color: themeColorTextDanger }}>
            <GelIcon
                width="24px"
                height="24px"
                color={themeColorTextDanger}
                name="AlertCircle"
                inline
            />
            {message}
        </GelLabel>
    );


    if (!contextData.memberDetails?.stateToken || !isFetched)
        return (<GelSpinner medium overlay />)

    return (
        <GelContainerLite style={{
            paddingRight: sizeNone,
            paddingLeft: sizeNone,
            paddingBottom: sizeBaseUnit * 15,
            paddingTop: sizeBaseUnit * 15, overflow: "hidden"
        }}>
            <GelBoxLayout alignment="start" space={[1, 4]} gutter="none">
                <GelForm
                    labelAtTop={true}
                    width={isXsScreen(screen) ?
                        '100%' : sizeBaseUnit * 110
                    }
                    {...!isXsScreen(screen) && {
                        labelWidth: sizeBaseUnit * 50
                    }}
                    fieldGutter={sizeBaseUnit * 10}
                    disableOnSubmit
                    onSubmit={onSubmit}
                    reset={resetForm}
                    onReset={onFormDataReset}
                    parseResponseBody={false}
                    style={{
                        paddingRight: sizeBaseUnit * 16,
                        paddingLeft: sizeBaseUnit * 16
                    }}
                >
                    <GelRowLayout gutter="medium" style={{ paddingLeft: sizeBaseUnit * 3 }}>
                        <GelHeading3>
                            {data?.headerText} {contextData.memberDetails?.mobileNumber}
                        </GelHeading3>

                        <GelParagraph style={{ paddingTop: sizeBaseUnit * 2 }}>{data?.subText}</GelParagraph>
                        <div style={{ paddingBottom: sizeBaseUnit * 2 }}>
                            <GelLabel style={{
                                fontWeight: 100
                            }}>
                                {data?.headerSubText}
                            </GelLabel>
                        </div>
                        <GelFormField
                            label={data?.verificationCodeText}
                        >
                            <GelTextInput
                                name="verificationCode"
                                onChange={onFormDataChange}
                                value={formData.verificationCode}
                                required
                                requiredErrorMsg={data?.errorMessageContent.verifyCodeReqMsg}
                            />

                        </GelFormField>

                        {mfaInvalidCodeMessage && <MFAErrorMessage message={mfaInvalidCodeMessage} />}
                        {mfaStatusMessage && <MFAErrorMessage message={mfaStatusMessage} />}

                        <GelFormField style={{ paddingTop: sizeBaseUnit * 4 }}>
                            <GelButton
                                name="" style={{
                                    width: "100%",
                                }}
                                primary large submit
                                disabled={isAccountLocked}
                            >
                                {data?.verifyButtonText}
                            </GelButton>
                        </GelFormField>

                        <div style={{ paddingTop: sizeBaseUnit * 4 }}>
                            <GelLabel style={{
                                fontWeight: 100,
                                paddingRight: sizeBaseX2
                            }}>
                                {data?.didNotReceiveCodeText}
                                <GelLink style={{ marginLeft: 4 }} onClick={ReSendMfaCode}>
                                    {data?.resendCodeText}
                                </GelLink>
                            </GelLabel>
                        </div>

                        <div style={{ paddingBottom: sizeBaseUnit * 4 }}>
                            <GelLabel style={{
                                fontWeight: 100,
                                paddingRight: sizeBaseX2
                            }}>
                                {data?.notYourMobileText}
                                <GelLink style={{ marginLeft: 4 }} onClick={navigateToMobileUpdate}>
                                    {data?.resetMobileLink}
                                </GelLink>
                            </GelLabel>
                        </div>
                    </GelRowLayout>
                </GelForm>
                {isLargerThanSmScreen(screen) &&
                    <FundBanner />
                }
            </GelBoxLayout>
        </GelContainerLite>

    )
}

export default TwoFactorAuth;